import type { SyncFrameOrientation, SyncSpreadAdaptationDTO } from '@lite-workspace/dto-fotoclass-book';
import { genitiveFullName, pluralRu } from '@lite-workspace/shared/format';

/** One album of a run where the engine re-picked frames under the student's photos. */
export interface AdaptedAlbum {
  personId: string;
  photobookId: string;
  name: string;
  /** «Алины»; `null` = the name does not decline with certainty. */
  firstNameGenitive: string | null;
  avatar: string | null;
  spreads: readonly SyncSpreadAdaptationDTO[];
}

/** User-level «Больше не показывать» of the report: one dismissed flow on the profile. */
export const SYNC_ADAPTED_REPORT_FLOW = 'sync-adapted-frames-report';

export const adaptedAlbumsText = (k: number): string =>
  `В ${k} ${pluralRu(k, ['альбоме', 'альбомах', 'альбомах'])} подобрали рамки под фото ${k === 1 ? 'ученика' : 'учеников'}`;

export const adaptedAlbumsLead = (k: number, sourceIsEtalon: boolean): string => {
  const who = k === 1 ? 'У этого ученика' : 'У этих учеников';

  return `${who} меньше подходящих фото, чем рамок ${sourceIsEtalon ? 'в эталоне' : 'в исходном альбоме'}.`;
};

export const spreadLabel = (spreadIndex: number): string => `Разворот ${spreadIndex + 1}`;

/** «Разворот 4» / «Развороты 2, 4, 5». */
export function spreadsLine(spreads: readonly SyncSpreadAdaptationDTO[]): string {
  const numbers = spreads.map(spread => spread.spreadIndex + 1);
  return `${numbers.length === 1 ? 'Разворот' : 'Развороты'} ${numbers.join(', ')}`;
}

/** «Алина Андронова» → «Алины»: only a form the full-name declension is sure of. */
export function firstNameGenitive(fullName: string): string | null {
  const declined = genitiveFullName(fullName);
  return declined === fullName ? null : (declined.split(/\s+/)[0] ?? null);
}

const CARDINAL_GENITIVE = ['', 'одного', 'двух', 'трёх', 'четырёх', 'пяти', 'шести', 'семи', 'восьми', 'девяти', 'десяти'];
const ORDINAL_GENITIVE = [
  '',
  'первого',
  'второго',
  'третьего',
  'четвёртого',
  'пятого',
  'шестого',
  'седьмого',
  'восьмого',
  'девятого',
  'десятого',
];

const ORIENTATION_PLURAL_GENITIVE: Record<SyncFrameOrientation, string> = {
  vertical: 'вертикальных',
  horizontal: 'горизонтальных',
  square: 'квадратных',
};
const ORIENTATION_SINGULAR_GENITIVE: Record<SyncFrameOrientation, string> = {
  vertical: 'вертикального',
  horizontal: 'горизонтального',
  square: 'квадратного',
};
const ORIENTATION_SINGULAR_NEUTER: Record<SyncFrameOrientation, string> = {
  vertical: 'вертикальное',
  horizontal: 'горизонтальное',
  square: 'квадратное',
};
const ORIENTATION_PLURAL: Record<SyncFrameOrientation, string> = {
  vertical: 'вертикальные',
  horizontal: 'горизонтальные',
  square: 'квадратные',
};

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** «Вертикальных групповых фото у Алины меньше двух. Вместо второго вертикального поставили горизонтальное.» */
export function adaptationReason(adaptation: SyncSpreadAdaptationDTO, genitive: string | null): string {
  const { missing, replacedBy, albumFrames } = adaptation;
  if (!missing) return `Рамки на развороте переставили под фото ${genitive ?? 'ученика'}.`;

  const orientation = missing.orientation;
  const kindNoun = missing.kind === 'portrait' ? 'портретов' : missing.kind === 'group' ? 'групповых фото' : null;
  const subject = capitalize(
    [orientation ? ORIENTATION_PLURAL_GENITIVE[orientation] : null, kindNoun ?? (orientation ? 'фото' : 'подходящих фото')]
      .filter(Boolean)
      .join(' '),
  );
  const owner = `у ${genitive ?? 'ученика'}`;
  const shortfall =
    missing.etalonCount <= 1
      ? `${subject} ${owner} нет.`
      : `${subject} ${owner} меньше ${CARDINAL_GENITIVE[missing.etalonCount] ?? missing.etalonCount}.`;

  return `${shortfall} ${remedy(orientation, replacedBy, missing.etalonCount, missing.albumCount, albumFrames)}`;
}

function remedy(
  orientation: SyncFrameOrientation | undefined,
  replacedBy: SyncFrameOrientation | undefined,
  etalonCount: number,
  albumCount: number,
  albumFrames: number,
): string {
  if (!orientation || !replacedBy) return `Подобрали шаблон на ${albumFrames} фото.`;
  if (etalonCount - albumCount > 1) {
    return `Вместо ${ORIENTATION_PLURAL_GENITIVE[orientation]} поставили ${ORIENTATION_PLURAL[replacedBy]}.`;
  }

  const which = etalonCount > 1 ? `${ORDINAL_GENITIVE[albumCount + 1] ?? `${albumCount + 1}-го`} ` : '';

  return `Вместо ${which}${ORIENTATION_SINGULAR_GENITIVE[orientation]} поставили ${ORIENTATION_SINGULAR_NEUTER[replacedBy]}.`;
}
